import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { CloseIcon } from '../icons/ControlIcons';
import { FitScreenIcon, ZoomInIcon } from '../icons/ViewerIcons';
import { artSources } from './carArt';
import CarViewerSlide from './CarViewerSlide';

/**
 * Every picture of one car, at screen size, and as far into any of them as the
 * reader wants to pinch (SPEC-012 PR-12.3).
 *
 * The card crops its take to a stage that bleeds off the top of the screen; the
 * gallery band draws the rest at 78% of a phone's width in a 4:3 box with
 * `object-fit: cover`. Both are correct as furniture and both are the wrong way
 * to show somebody a car. This is the same artwork with none of that — the whole
 * of each take, fitted to the screen on the car's own livery, in the order the
 * record authors them.
 *
 * A real `<dialog>` opened with `showModal()`, because focus trapping, Escape,
 * the top layer and an inert background are four things the platform gets right
 * and a hand-rolled overlay gets subtly wrong. The top layer is also what lets
 * this stay in the tree under `.car-detail` — no portal, and `--livery` still
 * arrives by inheritance, so the cut-outs multiply into their own car's colour
 * here exactly as they do on the card (SPEC-012 D-11).
 *
 * IT HOLDS EVERY TAKE, not the gallery's list. The gallery is the takes the card
 * is NOT showing, which is why ten of the twenty-four cars have no gallery at all
 * — their delivery was one picture and the card is already showing it. This is
 * the one place in the product that holds all of a car's pictures, and that is
 * the whole reason it exists (D-10).
 */
export default function CarViewer({ car, startTake, onClose }) {
    const t = useTranslate();
    const sources = artSources(car);
    const dialogRef = useRef(null);
    const railRef = useRef(null);
    // One imperative handle per slide, so the chrome up here can aim `reset` and
    // `toggleZoom` at whichever picture is showing without the view itself ever
    // reaching React (SPEC-012 D-13). Ref OBJECTS, allocated once per slide: an
    // inline callback ref would be a new function every render, and React
    // detaches and re-attaches a ref whose identity changed.
    const slideControls = useRef([]);
    const controlsFor = useCallback((index) => {
        slideControls.current[index] ??= { current: null };

        return slideControls.current[index];
    }, []);
    // Where the reader asked to start. Resolved to an index rather than carried
    // as a take, because everything below counts pages: a take the record does
    // not have opens on the first picture rather than on nothing.
    const startIndex = Math.max(sources.findIndex((source) => source.take === startTake), 0);
    const [activeSlide, setActiveSlide] = useState(startIndex);
    // WHICH slide is zoomed, rather than a bare boolean: the button's label has
    // to describe the picture in front of the reader, and a slide reporting
    // itself fitted must not answer for the one they are actually looking at.
    const [zoomedSlide, setZoomedSlide] = useState(null);
    // The `close` listener is bound once, but has to report the take the reader
    // actually left on — a ref, so the binding never goes stale.
    const leftOnRef = useRef(startIndex);

    const trackScroll = useCallback((event) => {
        const rail = event.currentTarget;
        const index = Math.round(rail.scrollLeft / Math.max(rail.clientWidth, 1));

        leftOnRef.current = index;
        setActiveSlide(index);
    }, []);

    const onZoomChange = useCallback((index, fitted) => {
        setZoomedSlide((current) => {
            if (fitted) {
                return current === index ? null : current;
            }

            return index;
        });
    }, []);

    useEffect(() => {
        const dialog = dialogRef.current;
        // One exit for both ways out: the button calls `close()`, Escape makes
        // the browser call it, and this fires either way.
        const reportClosed = () => onClose?.(sources[leftOnRef.current]?.take);

        dialog.addEventListener('close', reportClosed);
        dialog.showModal();
        // Opened ON the take the reader tapped rather than at the beginning: this
        // is that picture made whole, not a slideshow. Set after `showModal()`,
        // which is the first moment the rail has a width to multiply.
        railRef.current.scrollLeft = startIndex * railRef.current.clientWidth;

        return () => {
            dialog.removeEventListener('close', reportClosed);

            if (dialog.open) {
                dialog.close();
            }
        };
        // Deliberately mounted once, with an empty dependency list. `onClose` and
        // `sources` are read through the closure at the moment the dialog closes;
        // re-running this effect would re-open the dialog and throw away the
        // reader's place in the carousel.
    }, []);

    // A zoom belongs to the picture it was made on (SPEC-012 D-12). Arriving on
    // one already inside it, at an offset chosen for a different picture, would
    // make the reader's first move on every new take an undo.
    useEffect(() => {
        slideControls.current.forEach((controls, index) => {
            if (index !== activeSlide) {
                controls.current?.reset();
            }
        });
    }, [activeSlide]);

    // A rotation re-fits every picture and moves every bound, so a view computed
    // against the old screen is not merely stale, it is out of range.
    useEffect(() => {
        const refit = () => slideControls.current.forEach((controls) => controls.current?.reset());

        window.addEventListener('resize', refit);

        return () => window.removeEventListener('resize', refit);
    }, []);

    const name = carName(car);
    const isFitted = zoomedSlide === null;

    return (
        <dialog
            className="car-viewer"
            ref={dialogRef}
            aria-label={t('cars.viewer.title', { car: name })}
        >
            {/* First in the DOM so `showModal()` lands focus here, which is the
                one control somebody who opened this by accident is looking for. */}
            <button
                type="button"
                className="round-button car-viewer__close"
                onClick={() => dialogRef.current.close()}
                aria-label={t('cars.viewer.close')}
            >
                <CloseIcon size={22} />
            </button>

            {/* The pinch's single-pointer equivalent, and the only VISIBLE thing
                that says this screen zooms at all. A gesture is invisible to a
                screen reader and impossible for some hands, so it can never be
                the only way in. */}
            <button
                type="button"
                className="round-button car-viewer__zoom"
                onClick={() => slideControls.current[activeSlide]?.current?.toggleZoom()}
                aria-label={t(isFitted ? 'cars.viewer.zoomIn' : 'cars.viewer.fitToScreen')}
            >
                {isFitted ? <ZoomInIcon size={22} /> : <FitScreenIcon size={22} />}
            </button>

            <ul
                className="car-viewer__rail"
                ref={railRef}
                onScroll={trackScroll}
                tabIndex={0}
                role="group"
                aria-label={t('a11y.gallery', { car: name })}
            >
                {sources.map((source, index) => (
                    <CarViewerSlide
                        key={source.take}
                        source={source}
                        alt={t('a11y.galleryImage', { car: name, take: t(`cars.take.${source.take}`) })}
                        caption={t(`cars.take.${source.take}`)}
                        index={index}
                        railRef={railRef}
                        controlsRef={controlsFor(index)}
                        onZoomChange={onZoomChange}
                    />
                ))}
            </ul>

            {/* Decorative: the rail is a labelled group and each slide prints its
                own take, so a reader who cannot see these has been told where
                they are twice already. */}
            {sources.length > 1 ? (
                <ol className="car-viewer__dots" aria-hidden="true">
                    {sources.map((source, index) => (
                        <li
                            className={`car-viewer__dot${index === activeSlide ? ' car-viewer__dot--active' : ''}`}
                            key={source.take}
                        />
                    ))}
                </ol>
            ) : null}
        </dialog>
    );
}
